// Chemin: C:\gestion-copro\src\pages\public\SignalerPage.tsx
// Page de signalement protegee - ORION PILOT
// Session 8 : pre-remplissage infos utilisateur connecte
// Session 212 : ajout coproprieteId dans formData (étanchéité inter-copropriétés)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  SignalementFormData, 
  INITIAL_FORM_DATA,
  CATEGORIES,
  LOCALISATION_LABELS,
  URGENCE_LABELS
} from '../../types/signalement.types';
import { createSignalement } from '../../services/signalement.service';
import { compressImages } from '../../utils/imageCompressor';

const SignalerPage: React.FC = () => {
  const navigate = useNavigate();
  const { firebaseUser, userProfile, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState<SignalementFormData>({
    ...INITIAL_FORM_DATA,
    coproprieteId: ''
  });
  
  const [photos, setPhotos] = useState<File[]>([]);
  const [photosPreviews, setPhotosPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-remplir avec les donnees utilisateur + coproprieteId
  useEffect(() => {
    if (userProfile) {
      const coproprieteId =
        userProfile.coproprieteIds?.[0] ||
        `vigie-${userProfile.id}` ||
        '';

      setFormData(prev => ({
        ...prev,
        coproprieteId,
        nom: `${userProfile.prenom} ${userProfile.nom}`,
        email: userProfile.email || '',
        telephone: (userProfile as any).gsm || '',
        lot: (userProfile as any).lots 
          ? (userProfile as any).lots.map((l: any) => l.numero).join(', ') 
          : ''
      }));
    }
  }, [userProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files).slice(0, 5 - photos.length);
    
    for (const file of newFiles) {
      if (!file.type.startsWith('image/')) {
        setError('Seules les images sont acceptees');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Max 10 MB par image');
        return;
      }
    }

    try {
      const compressedFiles = await compressImages(newFiles, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.8
      });
      setPhotos(prev => [...prev, ...compressedFiles]);
      
      for (const file of compressedFiles) {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            setPhotosPreviews(prev => [...prev, event.target!.result as string]);
          }
        };
        reader.readAsDataURL(file);
      }
      setError(null);
    } catch (err) {
      setError('Erreur lors du traitement des images');
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titre.trim() || formData.titre.trim().length < 5) {
      newErrors.titre = 'Titre requis (min 5 caracteres)';
    }
    if (!formData.description.trim() || formData.description.trim().length < 20) {
      newErrors.description = 'Description requise (min 20 caracteres)';
    }
    if (!formData.localisationDetail.trim()) {
      newErrors.localisationDetail = 'Precision requise';
    }
    if (!formData.categorie) {
      newErrors.categorie = 'Categorie requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      const signalementData: SignalementFormData = {
        ...formData,
        photos
      };

      const result = await createSignalement(
        signalementData,
        firebaseUser?.uid
      );

      if (result.success && result.signalementId) {
        navigate('/confirmation', { 
          state: { 
            signalementId: result.signalementId,
            numero: result.numeroSignalement 
          } 
        });
      } else {
        setError(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Une erreur inattendue est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Signaler un probleme</h1>
          <p className="text-gray-600">Votre résidence</p>
        </div>

        {userProfile && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-600">Connecte en tant que</p>
            <p className="font-semibold text-blue-900">{userProfile.prenom} {userProfile.nom}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg ${errors.titre ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Ex: Infiltration hall entree"
            />
            {errors.titre && <p className="text-red-500 text-xs mt-1">{errors.titre}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Decrivez le probleme..."
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
              <select
                name="localisation"
                value={formData.localisation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.entries(LOCALISATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precision *</label>
              <input
                type="text"
                name="localisationDetail"
                value={formData.localisationDetail}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.localisationDetail ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Ex: Hall, Apt A12..."
              />
              {errors.localisationDetail && <p className="text-red-500 text-xs mt-1">{errors.localisationDetail}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categorie *</label>
              <select
                name="categorie"
                value={formData.categorie}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.categorie ? 'border-red-500' : 'border-gray-300'}`}
              >
                <option value="">Selectionnez...</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.categorie && <p className="text-red-500 text-xs mt-1">{errors.categorie}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Urgence *</label>
              <select
                name="urgence"
                value={formData.urgence}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.entries(URGENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photos (optionnel)</label>
            
            {photosPreviews.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {photosPreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img src={preview} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < 5 && (
              <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400">
                <span className="text-gray-500">+ Ajouter photos ({photos.length}/5)</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold text-white ${
              loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le signalement'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignalerPage;
